import * as React from 'react';
import {Alert, Button, Form, Modal} from 'antd';
import {FormComponentProps} from 'antd/lib/form';
import locales from '../locales';
import NumberFormItem from '../common/form-items/NumberFormItem';
import HiddenFormItem from '../common/form-items/HiddenFormItem';
import {ErrorAlert} from '../common/ErrorAlert';
import {ErrorResponse, InventoryStock, Item} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay,
} from '../app/ApplicationContext';
import {numberGroupFormatter, numberGroupParser} from '../utils/number-utils';
import ItemSelector from '../item/ItemSelector';
import {RentalState} from '../common/state';

const FIELD_COUNT = 'COUNT';
const FIELD_COST = 'cost';
const FIELD_ITEM = 'itemId';

interface Props extends FormComponentProps {
  inventoryStock: InventoryStock;
  onCancel: () => void;
  onConfirm: () => void;
}

interface State {
  error?: ErrorResponse;
  selectedItem?: Item;
  modalVisible: boolean;
  count?: number;
  itemSelectorVisible: boolean;
}

class InternalUsageModal extends React.Component<Props, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    modalVisible: true,
    itemSelectorVisible: false,
  };

  public render(): React.ReactNode {
    const {form, inventoryStock, onCancel} = this.props;
    const {error, selectedItem, itemSelectorVisible, modalVisible, count} = this.state;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Modal
          visible={modalVisible}
          title={locales.internally_used_add}
          okText={locales.save}
          cancelText={locales.cancel}
          onOk={this.onOk}
          onCancel={onCancel}
          destroyOnClose={true}
        >
          <Form>
            <HiddenFormItem
              form={form}
              fieldId={FIELD_ITEM}
              label={locales.item}
              initialValue={undefined}
              required={false}
            >
              {!selectedItem && (
                <Button icon="search" onClick={this.openItemSelector}>
                  {locales.select_item}
                </Button>
              )}
              {selectedItem && (
                <Alert
                  type="info"
                  message={selectedItem.name}
                  closable={true}
                  onClose={this.onItemCleared}
                />
              )}
            </HiddenFormItem>

            <NumberFormItem
              form={form}
              fieldId={FIELD_COUNT}
              label={locales.count}
              initialValue={count}
              required={true}
              min={1}
              onChange={this.onCountChange}
              precision={0}
            />

            <NumberFormItem
              form={form}
              fieldId={FIELD_COST}
              label={locales.initial_cost}
              initialValue={inventoryStock.infiniteStockCost}
              required={true}
              min={0}
              max={1_000_000_000}
              precision={0}
              parser={numberGroupParser}
              formatter={numberGroupFormatter}
            >
              <span style={{marginLeft: 8}}>{locales.app_currency}</span>
            </NumberFormItem>
          </Form>

          <ErrorAlert style={{marginTop: 16}} error={error}/>
        </Modal>

        {itemSelectorVisible && (
          <Modal
            visible={true}
            title={locales.select_item}
            footer={null}
            onCancel={this.closeItemSelector}
            width="auto"
            style={{margin: '0 24px'}}
            destroyOnClose={true}
          >
            <ItemSelector
              allowedStates={[RentalState.RENTED, RentalState.AVAILABLE]}
              selectedStates={[RentalState.RENTED, RentalState.AVAILABLE]}
              excludedFromSearch={[]}
              onSelect={this.onItemSelected}
            />
          </Modal>
        )}
      </>
    );
  }

  private onCountChange = (value: number | undefined) => {
    this.setState({count: value});
  }

  private openItemSelector = () => this.setState({itemSelectorVisible: true, modalVisible: false,});

  private closeItemSelector = () => this.setState({itemSelectorVisible: false, modalVisible: true});

  private onItemSelected = (item: Item) => {
    this.setState({selectedItem: item, itemSelectorVisible: false, modalVisible: true}, () => {
      this.props.form.setFieldsValue({[FIELD_ITEM]: item.id});
    });
  };

  private onItemCleared = () => {
    this.setState({selectedItem: undefined}, () => {
      this.props.form.setFieldsValue({[FIELD_ITEM]: undefined});
      this.props.form.validateFields([FIELD_ITEM], {force: true}, () => undefined);
    });
  };

  private onOk = () => {
    const {form, inventoryStock, onConfirm} = this.props;

    form.validateFields((err, values) => {
      if (err) {
        return;
      }

      const promise = furoApi.inventoryStockItems.createForInternalUsage({
        inventoryStockId: inventoryStock.id!,
        internallyUsedItemId: values[FIELD_ITEM],
        count: values[FIELD_COUNT],
        cost: values[FIELD_COST],
      })
        .then(onConfirm)
        .catch(this.onApiError);

      withLoadingOverlay(promise, this.loadingHandler);
    });
  };

  private onApiError = (error: ErrorResponse) => this.setState({error});
}

export default Form.create<Props>()(InternalUsageModal);



// WEBPACK FOOTER //
// ./src/inventory-stock-details/InternalUsageModal.tsx